import React, { use, useState } from 'react';
import { ToastContext, ToastTypes } from '../../contexts/toast.context';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import {
  DeviceFingerprintRow,
  useBlockDevice,
  useDeviceBlocklist,
  useDeviceFingerprintLookup,
  useUnblockDevice,
} from '../../hooks/useDeviceBlocklist';
import LoadingPage from '../common/LoadingPage';
import Header from '../layout/Header';

const inputClass =
  'block w-full sm:text-sm border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-gray-800 dark:border-dark-gray-600 dark:text-white px-3 py-2 border';

const thClass = 'px-3 py-3.5 text-left text-sm font-semibold text-gray-900 dark:text-dark-gray-100';
const tdClass = 'px-3 py-3 text-sm text-gray-700 dark:text-dark-gray-300 align-top';

const Signal: React.FC<{ label: string; on: boolean | null }> = ({ label, on }) =>
  on ? (
    <span className="inline-block rounded bg-yellow-100 text-yellow-800 px-1.5 py-0.5 text-xs mr-1">
      {label}
    </span>
  ) : null;

const DeviceBlocklist: React.FC = () => {
  const { createToast } = use(ToastContext);
  const { hasPermission, isLoading: permissionsLoading } = useAdminPermissions();
  const canView = hasPermission('device_blocklist.view');
  const canEdit = hasPermission('device_blocklist.edit');

  const [form, setForm] = useState({ email: '', clinicId: '', visitorId: '' });
  const [params, setParams] = useState<{ email?: string; clinicId?: string; visitorId?: string }>(
    {}
  );

  const { data: lookupData, isFetching: lookupFetching } = useDeviceFingerprintLookup(
    params,
    canView
  );
  const { data: blocklistData, isFetching: blocklistFetching } = useDeviceBlocklist(canView);
  const blockMutation = useBlockDevice();
  const unblockMutation = useUnblockDevice();

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const next: { email?: string; clinicId?: string; visitorId?: string } = {};
    if (form.email.trim()) next.email = form.email.trim().toLowerCase();
    if (form.clinicId.trim()) next.clinicId = form.clinicId.trim();
    if (form.visitorId.trim()) next.visitorId = form.visitorId.trim();
    setParams(next);
  };

  const handleBlock = async (device: DeviceFingerprintRow) => {
    const reason =
      window.prompt(
        `Reason for blocking this device? (${device.signupCount} signups)`,
        'Trial abuse'
      ) ?? undefined;
    // A blank string is a valid "block with no reason"; only a Cancel (null → undefined) aborts.
    if (reason === undefined) return;

    try {
      await blockMutation.mutateAsync({ visitorId: device.visitorId, reason });
      createToast({
        title: 'Device blocked',
        description: 'Future signups from this device will be rejected.',
        type: ToastTypes.Success,
        duration: 5000,
      });
    } catch (err: any) {
      createToast({
        title: 'Failed to block device',
        description: err?.response?.data?.message || err?.message || 'An error occurred.',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  };

  const handleUnblock = async (visitorId: string) => {
    try {
      await unblockMutation.mutateAsync(visitorId);
      createToast({
        title: 'Device unblocked',
        description: null,
        type: ToastTypes.Success,
        duration: 5000,
      });
    } catch (err: any) {
      createToast({
        title: 'Failed to unblock device',
        description: err?.response?.data?.message || err?.message || 'An error occurred.',
        type: ToastTypes.Fail,
        duration: 5000,
      });
    }
  };

  if (permissionsLoading) {
    return <LoadingPage />;
  }

  if (!canView) {
    return (
      <>
        <Header title="Device Blocklist" />
        <div className="p-6 text-sm text-gray-500 dark:text-dark-gray-400">
          You do not have permission to view the device blocklist.
        </div>
      </>
    );
  }

  const devices = lookupData?.items ?? [];
  const blocklist = blocklistData?.items ?? [];

  return (
    <>
      <Header title="Device Blocklist">
        <form onSubmit={onSearch} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-4">
          <input
            type="text"
            className={inputClass}
            placeholder="Email contains…"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
          />
          <input
            type="text"
            className={inputClass}
            placeholder="Clinic ID"
            value={form.clinicId}
            onChange={e => setForm(f => ({ ...f, clinicId: e.target.value }))}
          />
          <input
            type="text"
            className={inputClass}
            placeholder="Visitor ID"
            value={form.visitorId}
            onChange={e => setForm(f => ({ ...f, visitorId: e.target.value }))}
          />
          <button
            type="submit"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Search devices
          </button>
        </form>
      </Header>

      <div className="m-6 space-y-10">
        {/* Recorded devices */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-gray-100 mb-2">
            Recorded devices {lookupFetching ? '…' : `(${devices.length})`}
          </h3>
          <p className="text-xs text-gray-500 dark:text-dark-gray-400 mb-3">
            Sorted by signup count. Empty search shows the busiest devices — trial abuse is usually
            obvious here.
          </p>
          <div className="overflow-hidden shadow ring-1 ring-black/5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-dark-gray-700">
              <thead className="bg-gray-50 dark:bg-dark-gray-800">
                <tr>
                  <th className={thClass}>Visitor ID</th>
                  <th className={thClass}>Signups</th>
                  <th className={thClass}>Clinics</th>
                  <th className={thClass}>Emails</th>
                  <th className={thClass}>Signals</th>
                  <th className={thClass}>Last seen</th>
                  <th className={`${thClass} text-right`}>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-gray-700 bg-white dark:bg-dark-gray-900">
                {devices.length === 0 && (
                  <tr>
                    <td className={tdClass} colSpan={7}>
                      No devices found.
                    </td>
                  </tr>
                )}
                {devices.map(d => (
                  <tr
                    key={d.visitorId}
                    className={d.isBlocked ? 'bg-red-50 dark:bg-red-950/30' : ''}
                  >
                    <td className={`${tdClass} font-mono text-xs`}>{d.visitorId}</td>
                    <td className={tdClass}>{d.signupCount}</td>
                    <td className={tdClass}>{d.clinicCount}</td>
                    <td className={`${tdClass} max-w-xs truncate`} title={d.emails ?? ''}>
                      {d.emails}
                    </td>
                    <td className={tdClass}>
                      <Signal label="bot" on={!!d.bot && d.bot !== 'not_detected'} />
                      <Signal label="VPN" on={d.vpn} />
                      <Signal label="incognito" on={d.incognito} />
                      {d.velocity24h ? (
                        <span className="inline-block rounded bg-gray-100 text-gray-700 px-1.5 py-0.5 text-xs">
                          {d.velocity24h}/24h
                        </span>
                      ) : null}
                    </td>
                    <td className={tdClass}>
                      {d.lastSeen ? new Date(d.lastSeen).toLocaleString() : '—'}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {d.isBlocked ? (
                        <span className="text-red-600 font-medium text-xs">Blocked</span>
                      ) : canEdit ? (
                        <button
                          onClick={() => handleBlock(d)}
                          disabled={blockMutation.isLoading}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                        >
                          Block
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Current blocklist */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-gray-100 mb-3">
            Blocklist {blocklistFetching ? '…' : `(${blocklist.length})`}
          </h3>
          <div className="overflow-hidden shadow ring-1 ring-black/5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300 dark:divide-dark-gray-700">
              <thead className="bg-gray-50 dark:bg-dark-gray-800">
                <tr>
                  <th className={thClass}>Visitor ID</th>
                  <th className={thClass}>Reason</th>
                  <th className={thClass}>Blocked by</th>
                  <th className={thClass}>When</th>
                  <th className={`${thClass} text-right`}>Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-dark-gray-700 bg-white dark:bg-dark-gray-900">
                {blocklist.length === 0 && (
                  <tr>
                    <td className={tdClass} colSpan={5}>
                      Nothing blocked yet.
                    </td>
                  </tr>
                )}
                {blocklist.map(b => (
                  <tr key={b.visitorId}>
                    <td className={`${tdClass} font-mono text-xs`}>{b.visitorId}</td>
                    <td className={tdClass}>{b.reason || '—'}</td>
                    <td className={tdClass}>{b.blockedBy || '—'}</td>
                    <td className={tdClass}>
                      {b.createdAt ? new Date(b.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {canEdit ? (
                        <button
                          onClick={() => handleUnblock(b.visitorId)}
                          disabled={unblockMutation.isLoading}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 dark:bg-dark-gray-800 dark:text-dark-gray-200 dark:border-dark-gray-600"
                        >
                          Unblock
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default DeviceBlocklist;
